import './App.css';

import { COGNITO_PERMISSION_TO_ACCESS, GENERAL_ACCESS, RTS_ADMINS_GROUP } from '../../global/app-global-values';
import { Col, Container, Row } from 'react-bootstrap';
import { HashRouter, Redirect, Route, Switch } from 'react-router-dom';
import React, { useEffect, useState } from 'react';

import AppLayout from '@amzn/meridian/app-layout';
import LandingPage from '../../global/landing-page/LandingPage';
import { HIDE_MAIN_MENU_ON_SELECTION_KEY } from '../../global/local-storage-constants';
import Header from '../header/Header';
import Roller from 'react-spinners-css/dist/Roller';
import Sidebar from '../sidebar/Sidebar';
import StatusMessage from '../../global/status-message/StatusMessage';
import Text from '@amzn/meridian/text';
import UserProfile from '../../global/user-profile';
import { from } from 'rxjs';
import { getAllTenantIds, getTenantMetaData } from '../../global/api-calls';
import { getPayloadAttribute } from '../../global/jwt-utils';
import modules from '../../modules';
import { refreshTenantAwsAccounts } from '../../global/api-calls';

export const AccessGroupsContext = React.createContext([]);
export const TenantIdContext = React.createContext();
export const TenantMetadataContext = React.createContext();

const NoTenantsAvailable = () => {
  return <Text className="m-4">No tenants are available.</Text>;
};

const App = () => {
  const [currentModule, setCurrentModule] = useState('');
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [isUserAuthorizedToViewApp, setIsUserAuthorizedToViewApp] = useState(true);
  const [accessGroups, setAccessGroups] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [availableTenantIds, setAvailableTenantIds] = useState([]);
  const [tenantId, setTenantId] = useState(null);
  const [isRtsAdmin, setIsRtsAdmin] = useState(false);
  const [tenantMetaData, setTenantMetadata] = useState(null);

  const toggleIsSidebarOpen = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleIsSidebarOpenFromItemSelection = () => {
    if (localStorage.getItem(HIDE_MAIN_MENU_ON_SELECTION_KEY) === 'true') {
      toggleIsSidebarOpen();
    }
  };

  const computeModuleFromHash = () => {
    let locationNow = window.location.hash.replace('#', '');
    for (let m of modules) {
      if (locationNow.startsWith(m.routeProps.path) && currentModule !== m.name) {
        setCurrentModule(m.name);
        if (localStorage.getItem(HIDE_MAIN_MENU_ON_SELECTION_KEY) === 'true') {
          setIsSidebarOpen(false);
        }
        break;
      }
    }
  };

  computeModuleFromHash();
  window.addEventListener('hashchange', computeModuleFromHash);

  useEffect(() => {
    const jwtObservable = from(UserProfile.getJwtToken());
    const subscription = jwtObservable.subscribe(jwt => {
      let canViewApp = false;
      let isUserRtsAdmin = false;
      const tenantIds = new Set();
      const cognitoGroups = getPayloadAttribute(jwt, 'cognito:groups', [], false);
      for (const cognitoGroup of cognitoGroups) {
        // special treatment for RTS admins, the rest of the for loop doesn't apply to this group
        if (cognitoGroup === RTS_ADMINS_GROUP) {
          isUserRtsAdmin = true;
          canViewApp = true;
          break;
        }

        const cognitoGroupParts = cognitoGroup.split('@');
        // this should skip all groups that don't follow the pattern of permission@tenant_id
        if (cognitoGroupParts.length !== 2) {
          continue;
        }

        tenantIds.add(cognitoGroupParts[1]);

        if (COGNITO_PERMISSION_TO_ACCESS.has(cognitoGroupParts[0])) {
          for (const access of COGNITO_PERMISSION_TO_ACCESS.get(cognitoGroupParts[0])) {
            if (access === GENERAL_ACCESS) {
              canViewApp = true;
              break;
            }
          }
        }
      }

      if (canViewApp === true) {
        setIsUserAuthorizedToViewApp(true);
      } else {
        setIsUserAuthorizedToViewApp(false);
      }

      if (isUserRtsAdmin) {
        getAllTenantIds()
          .then(response => {
            const tenantIdsFromResponse = response.data;
            setAvailableTenantIds(tenantIdsFromResponse);
            setTenantId(tenantIdsFromResponse[0]);
            setIsAppLoading(false);
            setIsRtsAdmin(true);
          })
          .catch(() => {
            setAvailableTenantIds([]);
            setTenantId('');
            setIsAppLoading(false);
          });
      } else {
        const tenantIdsArray = Array.from(tenantIds);
        setAvailableTenantIds(tenantIdsArray);
        setTenantId(tenantIdsArray[0]);
        setIsAppLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!tenantId) {
      return;
    }
    refreshTenantAwsAccounts(tenantId);
    if (isRtsAdmin) {
      setAccessGroups(COGNITO_PERMISSION_TO_ACCESS.get(RTS_ADMINS_GROUP));
      return;
    }
    UserProfile.getJwtToken().then(jwt => {
      const cognitoGroups = getPayloadAttribute(jwt, 'cognito:groups', [], false);
      const accessGroupSet = new Set();
      for (const group of cognitoGroups) {
        const cognitoGroupParts = group.split('@');
        // this should skip all groups that don't follow the pattern of permission@tenant_id
        if (cognitoGroupParts.length !== 2) {
          continue;
        }

        if (cognitoGroupParts[1] === tenantId) {
          const role = cognitoGroupParts[0];
          if (COGNITO_PERMISSION_TO_ACCESS.has(role)) {
            COGNITO_PERMISSION_TO_ACCESS.get(role).forEach(val => accessGroupSet.add(val));
          }
        }
      }
      setAccessGroups(Array.from(accessGroupSet));
      const selectedModule = modules.find(module => module.name === currentModule);
      if (selectedModule && selectedModule.accessGroup && !accessGroupSet.has(selectedModule.accessGroup)) {
        setCurrentModule('');
      }
    });
  }, [tenantId, isRtsAdmin, currentModule]);

  useEffect(() => {
    if (!tenantId) {
      return;
    }
    getTenantMetaData(tenantId).then(response => {
      setTenantMetadata(response?.data || {});
    });
  }, [tenantId]);

  if (isAppLoading) {
    return (
      <Container fluid hidden={!isAppLoading} className="h-100v roller-glossy-bg">
        <Row className="justify-content-center h-100">
          <Col xs={3} sm={3} md={2} lg={1} xl={1} className="align-self-center">
            <Row>
              <label className="font-large">Loading...</label>
            </Row>
            <Row>
              <Roller color="grey" />
            </Row>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!isUserAuthorizedToViewApp) {
    return (
      <Container fluid className="h-100v roller-glossy-bg">
        <Row className="justify-content-center">
          <Col className="text-center align-self-center">
            <label className="font-30pt">You are unauthorized to access this resource</label>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <AccessGroupsContext.Provider value={accessGroups}>
      <TenantIdContext.Provider value={tenantId}>
        <TenantMetadataContext.Provider value={tenantMetaData}>
          <HashRouter>
            <AppLayout headerComponent={Header} sidebarComponent={Sidebar}>
              <Header
                toggleIsSidebarOpen={toggleIsSidebarOpen}
                onTenantIdChange={setTenantId}
                availableTenantIds={availableTenantIds}
              />
              <Sidebar
                open={isSidebarOpen}
                toggleOpen={toggleIsSidebarOpenFromItemSelection}
                accessGroups={accessGroups}
                currentModule={currentModule}
                setCurrentModule={setCurrentModule}
                areAnyTenantsAvailable={availableTenantIds && availableTenantIds.length > 0}
              />
              <div className="h-100">
                <div>
                  <StatusMessage />
                </div>
                <Switch>
                  <Route
                    exact
                    path="/"
                    render={() =>
                      availableTenantIds && availableTenantIds.length === 0 ? (
                        <NoTenantsAvailable />
                      ) : (
                        <LandingPage
                          modules={modules.filter(m => !m.accessGroup || accessGroups.includes(m.accessGroup))}
                        />
                      )
                    }
                  />
                  {modules
                    .filter(module => !module.accessGroup || accessGroups.includes(module.accessGroup))
                    .map(module => (
                      <Route {...module.routeProps} key={module.name} />
                    ))}
                  <Route path="*" render={() => <Redirect replace to={`/`} />} />
                  {availableTenantIds && availableTenantIds.length === 0 ? <Redirect replace to={'/'} /> : <></>}
                </Switch>
              </div>
            </AppLayout>
          </HashRouter>
        </TenantMetadataContext.Provider>
      </TenantIdContext.Provider>
    </AccessGroupsContext.Provider>
  );
};

export default App;
